import * as THREE from 'three';
import { litterKit, litterSpecimen, type LitterShape } from './nearDetail';

/**
 * THE THING IN THE HAND — Grasp's seconds horizon, which it did not have.
 *
 * `PLAYABLE.md`'s three-horizon table scored Grasp's first horizon as *a
 * notice on take; no held-object visual*: press `4` over a stone and the
 * world said "you take hold of a stone" in the chrome and drew nothing.
 * This draws it: the taken thing's own shape, in the ground layer's own
 * material, lifted a little over the ground under the pointer and turning
 * slowly, for as long as the hand is closed. Let go and it is gone from the
 * hand in the same frame the world puts it down.
 *
 * The shape and material are `render/nearDetail.ts`'s exports — the same
 * geometry the litter layer draws the thing with on the ground, so what is
 * lifted looks like what was lying there. Nothing here reads or writes the
 * simulation: the hold is `session.hold`, which `main.ts` owns, and the
 * only question asked of the world is the ground height under the pointer.
 *
 * Known and honest: the litter layer goes on drawing the thing where it lay
 * until the drop moves it — for the length of a hold there are two of it.
 * Hiding the lifted key from the disc is one line in `nearDetail.ts`'s
 * instance walk, and that file is Lane W's; the handoff carries the patch.
 */
export interface HeldThing {
  readonly group: THREE.Group;
  /**
   * Show the held thing over a point, or hide it. `kind` is the litter kind
   * the pointer named (`Target.kind === 'litter'` → `Target.name` is prose;
   * the kind rides in the target's id as `litter:<kind>:…` or is passed
   * explicitly by the caller), `size` its metres across.
   */
  hold(at: { x: number; y: number; z: number } | null, kind?: string, size?: number): void;
  update(tSeconds: number): void;
}

const SHAPES: ReadonlySet<string> = new Set<LitterShape>([
  'stone', 'stick', 'clump', 'log', 'shell', 'bone', 'fern', 'flower', 'mushroom', 'moss', 'seedhead',
]);

/** How high the hand holds it over the ground, and how much it breathes. */
const LIFT = 1.1;
const BOB = 0.12;

export function createHeldThing(): HeldThing {
  const group = new THREE.Group();
  const kit = litterKit();
  /**
   * NOT the kit's material. The litter layer's material carries a wind patch
   * that reads `instanceMatrix` — it only compiles on an `InstancedMesh` —
   * and the first cut put it on a plain `Mesh`: the console filled with
   * `'instanceMatrix' : undeclared identifier` and the hand drew nothing at
   * all while every structural check said it had. The shapes are the kit's;
   * the material is the kit's flat-shaded vertex-coloured look without the
   * wind, and a held thing does not sway in the wind in any case.
   */
  const material = new THREE.MeshStandardMaterial({
    roughness: 0.94, metalness: 0, vertexColors: true, flatShading: true,
  });
  let mesh: THREE.Mesh | null = null;
  let shownKind = '';
  let at: { x: number; y: number; z: number } | null = null;

  const build = (kind: string, size: number) => {
    if (mesh) { group.remove(mesh); mesh = null; }
    const shape = (SHAPES.has(kind) ? kind : 'stone') as LitterShape;
    const spec = litterSpecimen(shape, 0.5);
    const m = new THREE.Mesh(kit.shapes[shape], material);
    // the specimen's proportions at the thing's own size: the stone is as
    // wide as the pointer said it was, and as tall as a stone that wide is
    const [sw, sh, sd] = spec.scale;
    const k = size > 0 ? size / Math.max(1e-3, Math.max(sw, sd)) : 1;
    m.scale.set(sw * k, sh * k, sd * k);
    m.castShadow = true;
    m.frustumCulled = false;
    // the layer gives each instance its hue through the instance colour; a
    // lone mesh has none, so the specimen's hue is the material's own colour
    material.color.copy(spec.colour);
    mesh = m;
    group.add(m);
    shownKind = kind;
  };

  return {
    group,
    hold(where, kind = 'stone', size = 0.3) {
      at = where;
      if (!where) { if (mesh) mesh.visible = false; return; }
      if (!mesh || shownKind !== kind) build(kind, size);
      mesh!.visible = true;
    },
    update(t) {
      if (!mesh || !at || !mesh.visible) return;
      mesh.position.set(at.x, at.y + LIFT + Math.sin(t * 2.1) * BOB, at.z);
      mesh.rotation.y = t * 0.6;
      mesh.rotation.z = Math.sin(t * 1.3) * 0.12;
    },
  };
}
